import { useState, useEffect } from "react";
import { AddIcon, TrashIcon } from "assets";
import { InputForm, SubmitButton, Textarea } from "components";
import { QuationProps, QuestionInput } from "../../store/actionType";

interface QuestionnarieProps {
  initialQuestions?: QuationProps[];
  onChange?: (questions: QuestionInput[], isValid: boolean) => void;
  handleSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  onClose?: () => void;
  isUpdateMode?: boolean;
}

const Questionnarie: React.FC<QuestionnarieProps> = ({
  initialQuestions = [],
  handleSubmit,
  onChange,
  onClose,
  isUpdateMode,
}) => {
  // Always ensure at least one question exists
  const [questions, setQuestions] = useState<QuestionInput[]>(
    initialQuestions.length > 0
      ? initialQuestions
      : [
          {
            question: "",
            is_published: false,
            options: [{ option: "", is_correct_answer: false }],
          },
        ]
  );

  const [errors, setErrors] = useState<string[][]>([]);
  const [questionErrors, setQuestionErrors] = useState<string[]>([]);

  useEffect(() => {
    validateQuestions(questions);
  }, [questions]);

  const validateQuestions = (qs: QuestionInput[]) => {
    const newErrors: string[][] = [];
    const newQuestionErrors: string[] = [];
    let isValid = true;

    qs.forEach((q, qIndex) => {
      const optionErrors: string[] = [];
      if (!q.question.trim()) {
        newQuestionErrors[qIndex] = "Question cannot be empty.";
        isValid = false;
      } else {
        newQuestionErrors[qIndex] = "";
      }

      if (q.options.length < 2) {
        optionErrors.push("Each question must have at least 2 options.");
        isValid = false;
      }

      if (!q.options.some((opt) => opt.is_correct_answer)) {
        optionErrors.push("At least one option must be marked correct.");
        isValid = false;
      }

      q.options.forEach((opt, oIndex) => {
        if (!opt.option.trim()) {
          optionErrors.push(`Option ${oIndex + 1} cannot be empty.`);
          isValid = false;
        }
      });

      newErrors[qIndex] = optionErrors;
    });

    setErrors(newErrors);
    setQuestionErrors(newQuestionErrors);

    onChange?.(qs, isValid);
  };

  // Add a new empty question
  const handleAddQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      {
        question: "",
        is_published: false,
        options: [{ option: "", is_correct_answer: false }],
      },
    ]);
  };

  // Remove a question (but keep at least one)
  const handleRemoveQuestion = (qIndex: number) => {
    if (questions.length === 1) return;
    setQuestions(questions.filter((_, i) => i !== qIndex));
  };

  // Other handlers remain unchanged...
 const handleQuestionChange = (qIndex: number, value: string) => {
  setQuestions(prev => {
    const updated = prev.map((q, i) =>
      i === qIndex
        ? {
            ...q, 
            question: value,
          }
        : q
    );
    return updated;
  });
};


  const handleAddOption = (qIndex: number) => {
    setQuestions((prev) => {
      // Deep clone the questions and options
      const updated = prev.map((q) => ({
        ...q,
        options: q.options.map((opt) => ({ ...opt })),
      }));
      updated[qIndex].options.push({ option: "", is_correct_answer: false });
      return updated;
    });
  };
  const handleRemoveOption = (qIndex: number, oIndex: number) => {
    setQuestions((prev) => {
      const updated = prev.map((q) => ({
        ...q,
        options: q.options.map((opt) => ({ ...opt })),
      }));
      if (updated[qIndex].options.length === 1) return updated;
      updated[qIndex].options = updated[qIndex].options.filter(
        (_, i) => i !== oIndex
      );
      return updated;
    });
  };

const handleOptionChange = (qIndex: number, oIndex: number, value: string) => {
  setQuestions((prev) =>
    prev.map((q, i) => {
      if (i !== qIndex) return q;
      return {
        ...q,
        options: q.options.map((opt, j) =>
          j === oIndex
            ? { ...opt, option: value } 
            : opt
        ),
      };
    })
  );
};


  const handleCorrectAnswerChange = (qIndex: number, oIndex: number) => {
  setQuestions((prev) =>
    prev.map((q, i) => {
      if (i !== qIndex) return q;
      return {
        ...q,
        options: q.options.map((opt, j) => ({
          ...opt,
          is_correct_answer: j === oIndex,
        })),
      };
    })
  );
};


  const handleVisibilityChange = (qIndex: number, value: boolean) => {
    const updated = [...questions];
    updated[qIndex].is_published = value;
    setQuestions(updated);
  };

  return (
    <div className="bg-gray-50 text-black my-4 border rounded-lg shadow">
      <form>
        {questions.map((question, qIndex) => (
          <div
            key={qIndex}
            className="bg-gray-50 text-black rounded-md p-4 mb-4 py-4 px-6"
          >
            <Textarea
              label={`Enter Question ${qIndex + 1}`}
              value={question.question}
              onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
            />
            {questionErrors[qIndex] && (
              <p className="text-red-600 text-sm mt-1">
                {questionErrors[qIndex]}
              </p>
            )}

            {question.options.map((option, oIndex) => (
              <div key={oIndex} className="flex flex-col mt-2">
                <div className="flex items-center">
                  <div className="w-3/5">
                    <InputForm
                      label={`Option ${oIndex + 1}`}
                      value={option.option}
                      onChange={(e) =>
                        handleOptionChange(qIndex, oIndex, e.target.value)
                      }
                    />
                  </div>

                  <div className="flex flex-row gap-2 p-2">
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(qIndex, oIndex)}
                      className="p-1 hover:text-red-600"
                    >
                      <TrashIcon />
                    </button>

                    {oIndex === question.options.length - 1 && (
                      <button
                        type="button"
                        onClick={() => handleAddOption(qIndex)}
                        className="p-1 hover:text-green-600"
                      >
                        <AddIcon />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-2">
                  <span className="text-sm font-medium">
                    Is correct answer:
                  </span>
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="radio"
                      name={`correct-${qIndex}`}
                      className="accent-primary-500"
                      checked={option.is_correct_answer}
                      onChange={() => handleCorrectAnswerChange(qIndex, oIndex)}
                    />
                    Select
                  </label>
                </div>
              </div>
            ))}

            {errors[qIndex] && errors[qIndex].length > 0 && (
              <div className="mt-2">
                {errors[qIndex].map((err, i) => (
                  <p key={i} className="text-red-600 text-sm">
                    {err}
                  </p>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mt-2">
              <span className="text-sm font-medium">Visibility:</span>

              <label className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name={`visibility-${qIndex}`}
                  className="accent-primary-500"
                  checked={question.is_published}
                  onChange={() => handleVisibilityChange(qIndex, true)}
                />
                Publish
              </label>

              <label className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name={`visibility-${qIndex}`}
                  className="accent-primary-500"
                  checked={!question.is_published}
                  onChange={() => handleVisibilityChange(qIndex, false)}
                />
                Draft
              </label>
            </div>

            {!isUpdateMode && (
              <>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => handleRemoveQuestion(qIndex)}
                    className="p-2 hover:text-red-600"
                  >
                    <TrashIcon />
                  </button>

                  {qIndex === questions.length - 1 && (
                    <button
                      type="button"
                      onClick={handleAddQuestion}
                      className="p-2 hover:text-green-600"
                    >
                      <AddIcon />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        <div className="w-full flex justify-end p-4">
          <SubmitButton onClick={handleSubmit} />
        </div>
      </form>
    </div>
  );
};

export default Questionnarie;
